/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { observer } from "mobx-react-lite"

import type { ApkDetail, ApkDetailThreatLevelName } from "../../features/apk-detail/apk-detail-types"
import { formatEnum } from "../../utils/format-enum"
import { valueOrDash } from "../../utils/value-or-dash"
import { getRiskClassName } from "../components/risk-score"
import { Badge } from "../primitives/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"

import styles from "./apk-threat-level.module.css"

/**
 * Badge classes for each threat level.
 *
 * The keys cover every level the Package Detail API can return. Keep them in
 * sync with `ApkDetailThreatLevelName` so every level has a badge style.
 */
const ThreatLevelColor = {
    MALWARE: "bg-destructive/10 text-destructive",
    HIGHLY_DANGEROUS: "bg-orange-500/10 text-orange-700",
    DANGEROUS: "bg-amber-500/10 text-amber-700",
    POTENTIALLY_UNWANTED_APP: "bg-secondary text-secondary-foreground",
    SAFE: "bg-green-500/10 text-green-700",
    UNKNOWN: "bg-muted text-muted-foreground"
} as const satisfies Record<ApkDetailThreatLevelName, string>

/**
 * Returns the badge classes for a threat level.
 *
 * Unknown or missing names use the neutral `UNKNOWN` style.
 *
 * @param name - Threat level name from the API.
 * @returns The badge classes.
 */
function threatLevelColor(name: string | undefined) {
    // `Object.hasOwn` does not narrow `name`, hence the cast.
    return name && Object.hasOwn(ThreatLevelColor, name)
        ? ThreatLevelColor[name as ApkDetailThreatLevelName]
        : ThreatLevelColor.UNKNOWN
}

interface Props {
    /**
     * APK threat level and score to display.
     *
     * `score` is missing until the backend has scored the APK. The level's own
     * `score` is its fixed rank, which the card does not show.
     */
    apk: Pick<ApkDetail, "threatLevel" | "score">
}

/**
 * Displays the APK's threat level and score.
 *
 * The score uses the same colour bands as the device risk score. An unscored
 * APK shows `N/A`, and a missing threat level name shows a dash.
 *
 * @example
 * <ApkThreatLevel apk={apk} />
 */
export const ApkThreatLevel = observer(({ apk }: Props) => (
    <Card>
        <CardHeader>
            <CardTitle>
                <h3 className="m-0 text-xl font-bold uppercase">Threat Level</h3>
            </CardTitle>
        </CardHeader>
        <CardContent className="items-center">
            <Badge className={threatLevelColor(apk.threatLevel.name)}>
                {valueOrDash(formatEnum(apk.threatLevel.name))}
            </Badge>
            <p className={clsx("m-0 text-5xl font-semibold tabular-nums", styles.score, getRiskClassName(apk.score))}>
                <span className="sr-only">Score: </span>
                {apk.score === undefined ? (
                    "N/A"
                ) : (
                    <>
                        {apk.score}
                        <span className="text-xl text-muted-foreground">
                            {/* Keep the slash visual; screen readers hear "out of". */}
                            <span aria-hidden="true"> / </span>
                            <span className="sr-only"> out of </span>
                            100
                        </span>
                    </>
                )}
            </p>
        </CardContent>
    </Card>
))
